import { cn } from '@/lib/utils'
import type { AiResponseExcerpt as AiResponseExcerptData, SandboxBrand } from '../data/types'

// Token format: <brand id="acme">Acme CRM</brand> and <competitor id="salesforce">Salesforce</competitor>
const TOKEN_RE = /<(brand|competitor) id="([^"]+)">([^<]+)<\/\1>/g

type TokenType = 'brand' | 'competitor' | 'text'

interface TextSegment {
  type: TokenType
  id?: string
  content: string
}

function parseTokenMarkup(text: string): TextSegment[] {
  const segments: TextSegment[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  TOKEN_RE.lastIndex = 0

  while ((match = TOKEN_RE.exec(text)) !== null) {
    const fullMatch = match[0]
    const tokenType = match[1]
    const id = match[2]
    const content = match[3]
    const offset = match.index

    if (
      fullMatch === undefined ||
      tokenType === undefined ||
      id === undefined ||
      content === undefined
    ) {
      continue
    }

    if (offset > lastIndex) {
      segments.push({ type: 'text', content: text.slice(lastIndex, offset) })
    }

    if (tokenType === 'brand' || tokenType === 'competitor') {
      segments.push({ type: tokenType, id, content })
    }

    lastIndex = offset + fullMatch.length
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', content: text.slice(lastIndex) })
  }

  return segments
}

interface AiResponseExcerptProps {
  excerpt: AiResponseExcerptData
  className?: string
}

export function AiResponseExcerpt({ excerpt, className }: AiResponseExcerptProps) {
  const segments = parseTokenMarkup(excerpt.text)

  return (
    <div className={cn('space-y-3', className)}>
      {/* Response text with highlighted tokens */}
      <p className="text-sm leading-relaxed text-foreground">
        {segments.map((seg, i) => {
          if (seg.type === 'brand') {
            return (
              <span key={i} className="rounded-sm bg-emerald-bg px-0.5 text-emerald">
                {seg.content}
              </span>
            )
          }
          if (seg.type === 'competitor') {
            return (
              <span key={i} className="rounded-sm bg-info-soft px-0.5 text-info">
                {seg.content}
              </span>
            )
          }
          return <span key={i}>{seg.content}</span>
        })}
      </p>

      {/* Cited brands + sources */}
      {(excerpt.cited.length > 0 || excerpt.citedSources.length > 0) && (
        <div className="space-y-1 border-t border-border pt-2">
          {excerpt.cited.length > 0 && (
            <CitedList
              label="Brands mentioned"
              items={excerpt.cited.map((b: SandboxBrand) => b.name)}
            />
          )}
          {excerpt.citedSources.length > 0 && (
            <CitedList label="Sources cited" items={excerpt.citedSources} />
          )}
        </div>
      )}
    </div>
  )
}

function CitedList({ label, items }: { label: string; items: string[] }) {
  return (
    <p className="text-label text-muted-foreground">
      <span className="font-medium">{label}:</span> {items.join(', ')}
    </p>
  )
}
